import React, { useState } from 'react';
import { ArrowLeft, AlertTriangle, DollarSign } from 'lucide-react';
interface CashReconciliationProps {
  onComplete: (hasIssue: boolean) => void;
}
export const CashReconciliation: React.FC<CashReconciliationProps> = ({
  onComplete
}) => {
  const [currentRegister, setCurrentRegister] = useState('Register #1');
  const [denominations, setDenominations] = useState({
    hundreds: '',
    fifties: '',
    twenties: '',
    tens: '',
    fives: '',
    ones: '',
    quarters: '',
    dimes: '',
    nickels: '',
    pennies: ''
  });
  const [step, setStep] = useState(1);
  const handleDenominationChange = (denom: string, value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      setDenominations(prev => ({
        ...prev,
        [denom]: value
      }));
    }
  };
  const calculateTotal = () => {
    const values = {
      hundreds: parseInt(denominations.hundreds || '0') * 100,
      fifties: parseInt(denominations.fifties || '0') * 50,
      twenties: parseInt(denominations.twenties || '0') * 20,
      tens: parseInt(denominations.tens || '0') * 10,
      fives: parseInt(denominations.fives || '0') * 5,
      ones: parseInt(denominations.ones || '0') * 1,
      quarters: parseInt(denominations.quarters || '0') * 0.25,
      dimes: parseInt(denominations.dimes || '0') * 0.1,
      nickels: parseInt(denominations.nickels || '0') * 0.05,
      pennies: parseInt(denominations.pennies || '0') * 0.01
    };
    return Object.values(values).reduce((sum, value) => sum + value, 0).toFixed(2);
  };
  const expectedAmount = 1235.75;
  const countedAmount = parseFloat(calculateTotal());
  const variance = countedAmount - expectedAmount;
  const hasVariance = Math.abs(variance) > 0.01;
  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      onComplete(hasVariance);
    }
  };
  return <div className="space-y-4" data-id="element-11">
      <div className="flex items-center mb-4" data-id="element-12">
        <button onClick={() => onComplete(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 mr-2" data-id="element-13">
          <ArrowLeft className="w-5 h-5 text-gray-700" data-id="element-14" />
        </button>
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-15">
          Cash Reconciliation
        </h2>
      </div>
      {step === 1 && <>
          <div className="bg-white rounded-lg shadow p-4" data-id="element-16">
            <div className="flex justify-between items-center mb-4" data-id="element-17">
              <h3 className="font-medium text-gray-900" data-id="element-18">{currentRegister}</h3>
              <button className="text-sm text-blue-600" data-id="element-19">Change Register</button>
            </div>
            <div className="flex justify-between text-sm mb-2" data-id="element-20">
              <span className="text-gray-500" data-id="element-21">Expected Amount (POS)</span>
              <span className="font-medium" data-id="element-22">${expectedAmount.toFixed(2)}</span>
            </div>
            <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-23">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-24">
                Enter Cash Count
              </h4>
              <div className="grid grid-cols-2 gap-3" data-id="element-25">
                <div className="space-y-1" data-id="element-26">
                  <label className="text-xs text-gray-500" data-id="element-27">$100 Bills</label>
                  <div className="relative" data-id="element-28">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500" data-id="element-29">
                      <DollarSign className="w-4 h-4" data-id="element-30" />
                    </span>
                    <input type="text" value={denominations.hundreds} onChange={e => handleDenominationChange('hundreds', e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md" placeholder="0" data-id="element-31" />
                  </div>
                </div>
                <div className="space-y-1" data-id="element-32">
                  <label className="text-xs text-gray-500" data-id="element-33">$50 Bills</label>
                  <div className="relative" data-id="element-34">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500" data-id="element-35">
                      <DollarSign className="w-4 h-4" data-id="element-36" />
                    </span>
                    <input type="text" value={denominations.fifties} onChange={e => handleDenominationChange('fifties', e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md" placeholder="0" data-id="element-37" />
                  </div>
                </div>
                <div className="space-y-1" data-id="element-38">
                  <label className="text-xs text-gray-500" data-id="element-39">$20 Bills</label>
                  <div className="relative" data-id="element-40">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500" data-id="element-41">
                      <DollarSign className="w-4 h-4" data-id="element-42" />
                    </span>
                    <input type="text" value={denominations.twenties} onChange={e => handleDenominationChange('twenties', e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md" placeholder="0" data-id="element-43" />
                  </div>
                </div>
                <div className="space-y-1" data-id="element-44">
                  <label className="text-xs text-gray-500" data-id="element-45">$10 Bills</label>
                  <div className="relative" data-id="element-46">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500" data-id="element-47">
                      <DollarSign className="w-4 h-4" data-id="element-48" />
                    </span>
                    <input type="text" value={denominations.tens} onChange={e => handleDenominationChange('tens', e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md" placeholder="0" data-id="element-49" />
                  </div>
                </div>
                {/* More denominations would go here */}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-50">
                <div className="flex justify-between" data-id="element-51">
                  <span className="text-sm font-medium text-gray-900" data-id="element-52">
                    Total Counted
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-53">
                    ${calculateTotal()}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <button onClick={handleNext} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors mt-4" data-id="element-54">
            Continue
          </button>
        </>}
      {step === 2 && <>
          <div className="bg-white rounded-lg shadow p-4" data-id="element-55">
            <h3 className="font-medium text-gray-900 mb-4" data-id="element-56">
              Reconciliation Summary
            </h3>
            <div className="space-y-3" data-id="element-57">
              <div className="flex justify-between text-sm" data-id="element-58">
                <span className="text-gray-500" data-id="element-59">Expected Amount (POS)</span>
                <span className="font-medium" data-id="element-60">
                  ${expectedAmount.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-61">
                <span className="text-gray-500" data-id="element-62">Counted Amount</span>
                <span className="font-medium" data-id="element-63">${countedAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-gray-200" data-id="element-64">
                <span className="text-gray-900 font-medium" data-id="element-65">Variance</span>
                <span className={`font-medium ${hasVariance ? 'text-red-600' : 'text-green-600'}`} data-id="element-66">
                  ${variance.toFixed(2)}
                </span>
              </div>
            </div>
            {hasVariance && <div className="mt-4 p-3 bg-red-50 rounded-md flex" data-id="element-67">
                <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-68" />
                <div data-id="element-69">
                  <p className="text-sm font-medium text-gray-900" data-id="element-70">
                    Cash variance detected
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-71">
                    Manager approval will be required to proceed
                  </p>
                </div>
              </div>}
            {hasVariance && <div className="mt-4" data-id="element-72">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-73">
                  Variance Reason
                </label>
                <select className="w-full border border-gray-300 rounded-md py-2 px-3" data-id="element-74">
                  <option value="" data-id="element-75">Select a reason</option>
                  <option value="miscounted" data-id="element-76">Miscounted</option>
                  <option value="change-fund" data-id="element-77">Change fund adjustment</option>
                  <option value="payout" data-id="element-78">Cash payout not recorded</option>
                  <option value="other" data-id="element-79">Other (add note)</option>
                </select>
                <textarea className="w-full mt-3 border border-gray-300 rounded-md py-2 px-3" rows={3} placeholder="Add notes about the variance..." data-id="element-80"></textarea>
              </div>}
          </div>
          <button onClick={handleNext} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors mt-4" data-id="element-81">
            {hasVariance ? 'Submit for Approval' : 'Confirm & Continue'}
          </button>
        </>}
      {step === 3 && <>
          <div className="bg-white rounded-lg shadow p-4" data-id="element-82">
            <h3 className="font-medium text-gray-900 mb-4" data-id="element-83">Prepare Deposit</h3>
            <div className="space-y-4" data-id="element-84">
              <div className="p-3 bg-blue-50 rounded-md" data-id="element-85">
                <p className="text-sm text-gray-900" data-id="element-86">
                  Place cash in deposit bag #A-12345
                </p>
              </div>
              <div className="space-y-2" data-id="element-87">
                <label className="block text-sm font-medium text-gray-700" data-id="element-88">
                  Take a photo of the sealed deposit bag
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-89">
                  <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md text-sm font-medium" data-id="element-90">
                    Take Photo
                  </button>
                </div>
              </div>
              <div className="flex items-center" data-id="element-91">
                <input id="confirm" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-92" />
                <label htmlFor="confirm" className="ml-2 block text-sm text-gray-900" data-id="element-93">
                  I confirm the deposit has been prepared according to procedure
                </label>
              </div>
            </div>
          </div>
          <button onClick={handleNext} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors mt-4" data-id="element-94">
            Complete Cash Reconciliation
          </button>
        </>}
    </div>;
};